import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export interface CameraPreset {
  name: string;
  x: number;
  z: number;
  distance: number;
  pitch: number;
  yaw: number;
}

interface FlyState {
  startPos: THREE.Vector3;
  endPos: THREE.Vector3;
  startTarget: THREE.Vector3;
  endTarget: THREE.Vector3;
  t0: number;
  ms: number;
}

/**
 * OrbitControls with a floored pan distance: the stock controls scale pan speed
 * by the camera→target distance, so once you zoom in (or fly to a station) the
 * left-drag pan becomes painfully slow. We keep the 1:1 ground-grab far out,
 * but stop the speed from collapsing below a usable minimum so you can still
 * drag across a station/neighbourhood after zooming in.
 */
class GroundPanControls extends OrbitControls {
  /** World-space distance below which panning no longer slows down further. */
  panDistanceFloor = 800;

  // Override the internal pan (not part of the public typings). The base class
  // event handlers dispatch to `this._pan`, so this takes over for mouse/touch.
  _pan(deltaX: number, deltaY: number) {
    const element = this.domElement;
    if (!element) return;
    const cam = this.object as THREE.PerspectiveCamera;
    const offset = new THREE.Vector3().copy(cam.position).sub(this.target);
    const targetDistance =
      Math.max(offset.length(), this.panDistanceFloor) * Math.tan(((cam.fov / 2) * Math.PI) / 180);
    const internal = this as unknown as {
      _panLeft(d: number, m: THREE.Matrix4): void;
      _panUp(d: number, m: THREE.Matrix4): void;
    };
    internal._panLeft((2 * deltaX * targetDistance) / element.clientHeight, cam.matrix);
    internal._panUp((2 * deltaY * targetDistance) / element.clientHeight, cam.matrix);
  }
}

export class SceneView {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: GroundPanControls;
  private grid: THREE.GridHelper | null = null;
  private ground: THREE.Mesh | null = null;
  private verticalScale = 1;
  private contentRoot = new THREE.Group();

  private fly: FlyState | null = null;
  private zoomTarget: number;
  private lastTime = 0;
  private keys = new Set<string>();
  private followTarget: THREE.Vector3 | null = null;
  private cursorScreen: { x: number; y: number } | null = null;

  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      logarithmicDepthBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setClearColor(0x0b0f14, 1);
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0x0b0f14, 9000, 70000);

    this.camera = new THREE.PerspectiveCamera(
      55,
      container.clientWidth / container.clientHeight,
      1,
      90000,
    );
    this.camera.position.set(0, 1800, 1800);

    this.controls = new GroundPanControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.1;
    this.controls.maxPolarAngle = Math.PI * 0.495;
    this.controls.minDistance = 6;
    this.controls.maxDistance = 60000;
    // Google-Earth-style mouse mapping: left drag pans (grab the ground),
    // right drag rotates/orbits, wheel zooms toward the cursor.
    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    };
    this.controls.screenSpacePanning = true;
    // custom, frame-smoothed dolly for a much smoother zoom down to platform level
    this.controls.enableZoom = false;
    this.controls.zoomToCursor = false;
    this.renderer.domElement.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });

    this.zoomTarget = this.camera.position.distanceTo(this.controls.target);

    const ambient = new THREE.HemisphereLight(0x8899bb, 0x11151a, 1.1);
    this.scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(3000, 6000, 2000);
    this.scene.add(dir);

    this.scene.add(this.contentRoot);
    this.verticalScale = 1;
    this.contentRoot.scale.set(1, 1, 1);

    window.addEventListener('resize', () => this.onResize(container));
    this.setupKeyboard();
  }

  get root(): THREE.Group {
    return this.contentRoot;
  }

  private setupKeyboard() {
    const isTyping = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    };
    window.addEventListener('keydown', (e) => {
      if (isTyping(e)) return;
      const k = e.key.toLowerCase();
      if (!'wasdqe'.includes(k) || e.ctrlKey || e.metaKey || e.altKey) return;
      this.keys.add(k);
      this.fly = null;
      e.preventDefault();
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.key.toLowerCase());
    });
    window.addEventListener('blur', () => this.keys.clear());
  }

  private applyKeyboard(dt: number) {
    if (this.keys.size === 0 || dt <= 0) return;
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    forward.y = 0;
    if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
    forward.normalize();
    const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();

    const move = new THREE.Vector3();
    if (this.keys.has('w')) move.add(forward);
    if (this.keys.has('s')) move.sub(forward);
    if (this.keys.has('d')) move.add(right);
    if (this.keys.has('a')) move.sub(right);
    if (this.keys.has('e')) move.y += 1;
    if (this.keys.has('q')) move.y -= 1;
    if (move.lengthSq() === 0) return;
    move.normalize();

    const dist = this.camera.position.distanceTo(this.controls.target);
    const speed = Math.max(6, dist) * 0.9; // units per second, scaled with zoom
    const delta = move.multiplyScalar(speed * dt);
    this.camera.position.add(delta);
    this.controls.target.add(delta);
  }

  private onWheel(e: WheelEvent) {
    e.preventDefault();
    this.fly = null;
    // Google-Earth zoom-to-cursor: remember the cursor position (only when not
    // following a train, so zooming keeps the tracked train centred). The ray is
    // recomputed every frame from this position so the point under the cursor
    // stays fixed during the smooth zoom instead of drifting.
    this.cursorScreen = this.followTarget ? null : { x: e.clientX, y: e.clientY };
    const amount = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY), 150);
    // multiplicative (constant ratio) plus a small absolute component so the
    // step does not shrink to nothing near platform level
    const mult = Math.exp(amount * 0.0018);
    const abs = amount * 0.04;
    this.zoomTarget = THREE.MathUtils.clamp(
      this.zoomTarget * mult + abs,
      this.controls.minDistance,
      this.controls.maxDistance,
    );
  }

  private cursorRayDirection(clientX: number, clientY: number): THREE.Vector3 {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
    return new THREE.Vector3(nx, ny, 1).unproject(this.camera).sub(this.camera.position).normalize();
  }

  private updateZoom(dt: number) {
    if (this.fly || dt <= 0) return;
    const offset = this.camera.position.clone().sub(this.controls.target);
    const cur = offset.length();
    if (Math.abs(cur - this.zoomTarget) < 0.001) {
      this.cursorScreen = null;
      return;
    }
    const a = 1 - Math.exp(-dt / 0.07);
    const next = THREE.MathUtils.clamp(
      cur + (this.zoomTarget - cur) * a,
      this.controls.minDistance,
      this.controls.maxDistance,
    );
    if (this.cursorScreen) {
      const cursorDir = this.cursorRayDirection(this.cursorScreen.x, this.cursorScreen.y);
      // move the camera down the pointer ray by the (small) remaining delta
      const radiusDelta = cur - next;
      this.camera.position.addScaledVector(cursorDir, radiusDelta);
      // Re-anchor the orbit target onto the horizontal plane through the old
      // target (instead of placing it in the air in front of the camera), so
      // zooming into a far, oblique area keeps the target on the ground.
      const fwd = this.camera.getWorldDirection(new THREE.Vector3());
      const denom = fwd.y;
      if (Math.abs(denom) > 0.02) {
        const t = (this.controls.target.y - this.camera.position.y) / denom;
        if (t > 0) {
          this.controls.target.copy(this.camera.position).addScaledVector(fwd, t);
        }
      }
    } else {
      offset.setLength(next);
      this.camera.position.copy(this.controls.target).add(offset);
    }
  }

  setContentBounds(minX: number, maxX: number, minZ: number, maxZ: number) {
    const size = Math.max(maxX - minX, maxZ - minZ);
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;

    if (this.grid) this.scene.remove(this.grid);
    const gridSize = Math.ceil(size * 1.3 / 500) * 500;
    this.grid = new THREE.GridHelper(gridSize, gridSize / 500, 0x274052, 0x1a2836);
    this.grid.position.set(cx, 0, cz);
    (this.grid.material as THREE.Material).transparent = true;
    (this.grid.material as THREE.Material).opacity = 0.5;
    this.scene.add(this.grid);

    if (this.ground) this.scene.remove(this.ground);
    const groundGeo = new THREE.PlaneGeometry(gridSize, gridSize);
    const groundMat = new THREE.MeshBasicMaterial({
      color: 0x16313f,
      transparent: true,
      opacity: 0.12,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.ground = new THREE.Mesh(groundGeo, groundMat);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.set(cx, 0, cz);
    this.ground.renderOrder = -2;
    this.scene.add(this.ground);
  }

  setVerticalScale(s: number) {
    this.verticalScale = s;
    // vertical exaggeration only affects rendered height, not the source data
    this.contentRoot.scale.y = s;
  }

  getVerticalScale() {
    return this.verticalScale;
  }

  setGroundOpacity(o: number) {
    if (this.ground) {
      const m = this.ground.material as THREE.MeshBasicMaterial;
      m.opacity = o;
      m.visible = o > 0.001;
    }
    if (this.grid) {
      (this.grid.material as THREE.Material).opacity = 0.15 + o * 0.4;
    }
  }

  setGroundVisible(on: boolean) {
    if (this.ground) this.ground.visible = on;
  }

  flyTo(x: number, z: number, distance: number, pitch: number, yawDeg: number, ms = 700) {
    this.cursorScreen = null;
    const yaw = (yawDeg * Math.PI) / 180;    const endTarget = new THREE.Vector3(x, 0, z);
    const endPos = new THREE.Vector3(
      x + Math.sin(yaw) * distance * Math.cos(pitch),
      distance * Math.sin(pitch),
      z + Math.cos(yaw) * distance * Math.cos(pitch),
    );
    this.zoomTarget = endPos.distanceTo(endTarget);
    if (ms <= 0) {
      this.camera.position.copy(endPos);
      this.controls.target.copy(endTarget);
      this.fly = null;
      return;
    }
    this.fly = {
      startPos: this.camera.position.clone(),
      endPos,
      startTarget: this.controls.target.clone(),
      endTarget,
      t0: performance.now(),
      ms,
    };
  }

  /** Like flyTo, but keeps the current camera orientation (heading + pitch). */
  flyToPreservingOrientation(x: number, z: number, distance: number, ms = 700) {
    const offset = this.camera.position.clone().sub(this.controls.target);
    const len = offset.length() || 1;
    const pitch = Math.asin(THREE.MathUtils.clamp(offset.y / len, -1, 1));
    const yawRad = Math.atan2(offset.x, offset.z);
    this.flyTo(x, z, distance, pitch, (yawRad * 180) / Math.PI, ms);
  }

  private updateFly(now: number) {
    if (!this.fly) return;
    const t = Math.min(1, (now - this.fly.t0) / this.fly.ms);
    const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    this.camera.position.lerpVectors(this.fly.startPos, this.fly.endPos, e);
    this.controls.target.lerpVectors(this.fly.startTarget, this.fly.endTarget, e);
    if (t >= 1) this.fly = null;
  }

  /** Follow a moving world-space target with a fixed offset (smooth). */
  follow(target: THREE.Vector3) {
    this.followTarget = target.clone();
    this.fly = null;
    this.keys.clear();
    this.cursorScreen = null;
    this.controls.enablePan = false;
  }

  stopFollow() {
    this.followTarget = null;
    this.controls.enablePan = true;
    this.zoomTarget = this.camera.position.distanceTo(this.controls.target);
  }

  get isFollowing(): boolean {
    return this.followTarget !== null;
  }

  /** Keep the orbit target on the tracked object so it stays centred. */
  private updateFollow(dt: number) {
    if (!this.followTarget || dt <= 0) return;
    const a = 1 - Math.exp(-dt * 8);
    this.controls.target.lerp(this.followTarget, a);
  }

  private onResize(container: HTMLElement) {
    const w = container.clientWidth;
    const h = container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  render() {
    const now = performance.now();
    const dt = this.lastTime ? Math.min(0.1, (now - this.lastTime) / 1000) : 0;
    this.lastTime = now;
    if (this.followTarget) {
      this.updateFollow(dt);
      this.updateZoom(dt);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      return;
    }
    this.applyKeyboard(dt);
    this.updateFly(now);
    this.updateZoom(dt);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }
}

export function disposeGroup(group: THREE.Group) {
  group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[];
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else if (mat) mat.dispose();
  });
  group.clear();
}
